import axios from 'axios';
import { action, computed, IReactionDisposer, makeObservable, reaction } from 'mobx';

import { stopwatchesApi } from '@/api/stopwatchesApi';
import { yaMetrikaStore } from '@/entities/yaMetrika';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import {
  CreateStopwatchRequest,
  Stopwatch,
  StopwatchStatus,
  UpdateStopwatchRequest,
} from '@/types';
import { AnalyticsEvent } from '@/types/yametrika';
import { formatError } from '@/utils/formatError';

import { AlertsStore } from '../AlertsStore';

const POLLING_INTERVAL_MS = 30_000;
const MAX_STOPWATCHES = 10;

export class StopwatchStore extends LocalStore {
  private readonly _alertsStore: AlertsStore;

  private readonly _stopwatches = new ValueModel<Stopwatch[]>([]);
  private readonly _loadingStage = new LoadingStageModel();
  private _pollingTimer: ReturnType<typeof setInterval> | null = null;
  private _pollingReaction: IReactionDisposer | null = null;
  private _clockOffset = 0;

  constructor({ alertsStore }: { alertsStore: AlertsStore }) {
    super();
    this._alertsStore = alertsStore;

    makeObservable(this, {
      stopwatches: computed,
      hasRunning: computed,
      loadStopwatches: action.bound,
      createStopwatch: action.bound,
      updateStopwatch: action.bound,
      deleteStopwatch: action.bound,
      startStopwatch: action.bound,
      pauseStopwatch: action.bound,
      finishStopwatch: action.bound,
    });
  }

  get stopwatches(): Stopwatch[] {
    return this._stopwatches.value;
  }

  get loadingStage(): LoadingStageModel {
    return this._loadingStage;
  }

  get hasRunning(): boolean {
    return this._stopwatches.value.some((sw) => sw.status === StopwatchStatus.Running);
  }

  get clockOffset(): number {
    return this._clockOffset;
  }

  startPolling(): void {
    this.stopPolling();
    this.loadStopwatches();

    this._pollingReaction = reaction(
      () => this.hasRunning,
      (hasRunning) => {
        this._clearInterval();

        if (hasRunning) {
          this._pollingTimer = setInterval(() => {
            this.loadStopwatches();
          }, POLLING_INTERVAL_MS);
        }
      },
      { fireImmediately: true },
    );
  }

  stopPolling(): void {
    this._clearInterval();
    this._pollingReaction?.();
    this._pollingReaction = null;
  }

  async loadStopwatches(): Promise<void> {
    const isPolling = this._loadingStage.isSuccess;

    try {
      if (!isPolling) {
        this._loadingStage.loading();
      }

      const items = await stopwatchesApi.list();
      this._stopwatches.change(this._sortStopwatches(items));
      this._loadingStage.success();
    } catch (error) {
      if (!isPolling) {
        this._loadingStage.error();
      }

      this._handleError(error);
    }
  }

  async createStopwatch(data: CreateStopwatchRequest): Promise<Stopwatch | null> {
    if (this._stopwatches.value.length >= MAX_STOPWATCHES) {
      this._alertsStore.addAlert({
        title: 'Лимит секундомеров',
        message: `Максимум ${MAX_STOPWATCHES} секундомеров`,
        withReload: false,
        autoClose: true,
      });

      return null;
    }

    try {
      const created = await stopwatchesApi.create(data);
      this._stopwatches.change(this._sortStopwatches([...this._stopwatches.value, created]));
      yaMetrikaStore.sendEvent(AnalyticsEvent.stopwatchCreate);

      return created;
    } catch (error) {
      this._handleError(error);

      return null;
    }
  }

  async updateStopwatch(id: string, data: UpdateStopwatchRequest): Promise<Stopwatch | null> {
    try {
      const updated = await stopwatchesApi.update(id, data);
      this._replaceStopwatch(id, updated);

      return updated;
    } catch (error) {
      this._handleError(error);

      return null;
    }
  }

  async deleteStopwatch(id: string): Promise<void> {
    try {
      await stopwatchesApi.remove(id);
      this._stopwatches.change(this._stopwatches.value.filter((sw) => sw.id !== id));
      yaMetrikaStore.sendEvent(AnalyticsEvent.stopwatchDelete);
    } catch (error) {
      this._handleError(error);
    }
  }

  async startStopwatch(id: string): Promise<Stopwatch | null> {
    try {
      const updated = await stopwatchesApi.start(id);

      if (updated.startedAt) {
        this._clockOffset = Date.now() - new Date(updated.startedAt).getTime();
      }

      this._replaceStopwatch(id, updated);
      yaMetrikaStore.sendEvent(AnalyticsEvent.stopwatchStart);

      return updated;
    } catch (error) {
      this._handleError(error);

      return null;
    }
  }

  async pauseStopwatch(
    id: string,
    accumulatedSeconds: number,
    startedAt: string,
  ): Promise<Stopwatch | null> {
    try {
      const updated = await stopwatchesApi.pause(id, { accumulatedSeconds, startedAt });
      this._replaceStopwatch(id, updated);
      yaMetrikaStore.sendEvent(AnalyticsEvent.stopwatchPause);

      return updated;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await this.loadStopwatches();
        this._alertsStore.addAlert({
          title: 'Конфликт',
          message: 'Данные секундомера изменились. Состояние обновлено.',
          withReload: false,
          autoClose: true,
        });

        return null;
      }

      this._handleError(error);

      return null;
    }
  }

  async finishStopwatch(id: string): Promise<void> {
    try {
      await stopwatchesApi.finish(id);
      this._stopwatches.change(this._stopwatches.value.filter((sw) => sw.id !== id));
      yaMetrikaStore.sendEvent(AnalyticsEvent.stopwatchFinish);
    } catch (error) {
      this._handleError(error);
    }
  }

  destroy(): void {
    this.stopPolling();
    super.destroy();
  }

  private _clearInterval(): void {
    if (this._pollingTimer) {
      clearInterval(this._pollingTimer);
      this._pollingTimer = null;
    }
  }

  private _sortStopwatches(stopwatches: Stopwatch[]): Stopwatch[] {
    return [...stopwatches].sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }

  private _replaceStopwatch(id: string, updated: Stopwatch): void {
    this._stopwatches.change(
      this._sortStopwatches(this._stopwatches.value.map((sw) => (sw.id === id ? updated : sw))),
    );
  }

  private _handleError(error: unknown): void {
    const formatted = formatError(error);
    this._alertsStore.addAlert({
      title: formatted.title,
      message: formatted.message,
      withReload: false,
      autoClose: true,
    });
  }
}
